/**
 * Shared English catalog for user-facing text generated outside the Next.js apps
 * (newsletters, transactional emails, reader-facing error messages, SEO templates)
 * plus strings both apps need. ICU message syntax (use-intl / next-intl compatible).
 *
 * IMPORTANT: keep in sync with the mirrored copy at packages/functions/src/shared/i18n/en.ts
 * (Lambdas resolve @shared/* to packages/functions/src/shared). A unit test enforces this.
 */
export const en = {
  newsletter: {
    byAuthor: 'By {author}',
    readMore: 'Read more →',
    rssPreviewPlaceholder: 'No items yet — fill in an RSS feed URL to see live items.',
    managePreferences: 'Manage preferences',
    unsubscribe: 'Unsubscribe',
  },
  email: {
    poweredBy: 'Powered by PubGen.AI',
    subscribedToNewsletterTitle: 'Subscribed to Newsletter',
    subscribedToNewslettersTitle: 'Subscribed to Newsletters',
    subscribedToNewsletterBody: 'You have been subscribed to {newsletterName}.',
    subscribedToNewslettersIntro: 'You have been subscribed to the following newsletters:',
    subscribedToNewslettersText: 'You have been subscribed to: {newsletterNames}.',
    willReceiveUpdatesAt: 'You will receive updates at {email}.',
    subscriptionSuccessTitle: 'Subscribed Successfully',
    subscriptionSuccessBody: 'Your subscription has been successfully processed.',
    subscriptionSuccessBodyWithProduct: 'Your subscription for {product} has been successfully processed.',
    subscriptionCancelledTitle: 'Subscription Cancelled',
    subscriptionCancelledBody: 'Your subscription has been cancelled.',
    subscriptionCancelledBodyWithProduct: 'Your subscription "{product}" has been cancelled.',
    willNoLongerReceiveUpdatesAt: 'You will no longer receive updates at {email}.',
    signInTitle: 'Sign In to {siteName}',
    signInMagicLinkBody: 'Please sign in by clicking the link below. This link will remain valid for 24 hours.',
    signInCodeBody: 'Please sign in by using the code below. This code will remain valid for 24 hours.',
    signInInAppHint:
      "Signing in from the Gmail or Facebook app? No worries — you may just need to sign in again once you're in your browser.",
    signInCta: 'Sign In',
    signInMagicLinkText:
      'Please sign in by clicking the link below. This link will remain valid for 24 hours at {siteName}: {magicLink}.\nIf you did not request this sign in, you can safely ignore this email.',
    signInCodeText:
      'Please sign in by using the code below. This code will remain valid for 24 hours at {siteName}: {code}.\nIf you did not request this sign in, you can safely ignore this email.',
    donationTitle: 'Thank you for your donation',
    donationBody: 'Your donation to {brandName} has been successfully processed.',
    donationContact: 'If you have any questions, please contact us at {contactUrl}.',
    giftCodeTitle: 'Subscription Gift Code',
    giftCodeBody:
      'You have purchased a subscription gift. Send the following link to the recipient so they can redeem their gift.',
    preferencesSubject: 'Manage your {siteName} email preferences',
    preferencesTitle: 'Manage your email preferences',
    preferencesBody: 'Use the link below to manage which {siteName} emails you receive, or to unsubscribe.',
    preferencesCta: 'Manage preferences',
    printOrderConfirmedTitle: 'Your print order is confirmed',
    printOrderConfirmedBody: 'Thanks for your order! Your {quantity} x {productType} is being prepared.',
    orderReference: 'Order reference: {ref}',
    printOrderTrackCta: 'Track your order',
    printOrderTrackAt: 'Track your order at {url}',
    printOrderTrackingTitle: 'Tracking information for your order',
    printOrderTrackingBody: 'Your order has shipped. Tracking number: {trackingNumber}.',
    printOrderViewShipmentCta: 'View shipment',
    printOrderShippedTitle: 'Your print order is on its way',
    printOrderShippedBody: 'Your order is in transit.',
    printOrderShippedTracking: 'Tracking: {trackingNumber}.',
    printOrderDeliveredTitle: 'Your print order was delivered',
    printOrderDeliveredBody: 'Your order has been delivered. We hope you love it.',
    printOrderDetailsCta: 'Order details',
    printOrderDetailsAt: 'Details at {url}',
    printOrderCancelledTitle: 'Your print order was cancelled',
    printOrderCancelledBody: "Your order has been cancelled. If you weren't expecting this, please reach out.",
    customerServiceEmailOnly: 'Customer service: email {email}.',
    customerServicePhoneOnly: 'Customer service: call {phone}.',
    customerServiceBoth: 'Customer service: email {email} or call {phone}.',
  },
  errors: {
    generic: 'An error occurred, please try again later.',
    siteNotConnectedToStripe: 'This site is not connected to a payment account.',
    subscriptionPlanNotFound: 'Subscription plan not found.',
    subscriptionAlreadyExists: 'A subscription with this email already exists.',
    printOnDemandNotConfigured: 'Print on demand is not configured for this site.',
    printOrderNotFound: 'Order not found.',
    newsletterNotAvailable: 'One or more newsletters are not available for subscription.',
    newsletterVerificationFailed: 'Verification failed. Please try again.',
    newsletterSubscribeFailed: 'We could not subscribe you at this time. Please try again later.',
  },
  // CMS-facing friendly messages keyed by ERROR_TYPES (see getFriendUserErrorMessageFromErrorType).
  errorTypes: {
    generic: 'An error occurred, please try again later.',
    genericWithCode: 'An error occurred, please try again later. (Error Code: {errorType}) ',
    AlreadySubscribed: 'Subscriber with this email already has for this publication.',
    CreateSubscriptionError: 'An error occurred while processing the subscription. Please try again later.',
    CancelSubscriptionError: 'An error occurred while canceling the subscription. Please try again later.',
    ListSubscriptionError: 'An error occurred while listing the subscriptions. Please try again later.',
    Unauthorized: 'You are not authorized to perform this action.',
    HeadlineGeneration: 'An error occurred while generating the headline, please try again later.',
    NoArticle: 'The article could not be downloded. Please try again later.',
    DomainFormatError: 'The domain format is not correct. Please use a valid domain format.',
    DomainUnavailable: 'The domain is already in use. Please use a different domain.',
    DomainNotConnected: 'The domain is not connected to the site. Please connect the domain to the site.',
    NoDomainConnected: 'No domain is connected to the site. Please connect a domain to the site.',
    NewsLetterNotFound: 'The newsletter could not be found. Please try again later.',
    WrongSlugFormat: 'The slug format is incorrect. Please use a slug that starts and ends with a forward slash.',
    SlugAlreadyExists:
      'A page with this slug already exists. If you are creating a new page, please change the page title, otherwise please choose a different slug.',
    NameAlreadyExists:
      'A section, tag, or writer with this name already exists on this site. Please choose a different name.',
    StripeNotConnected: 'The Stripe account is not connected. Please connect the Stripe account to the site.',
    SubdomainWrongFormat: 'The subdomain format is incorrect. Please use a valid subdomain format.',
    DomainNotFound: 'The domain could not be found. Please try again later.',
    WorkspaceNotFound: 'The workspace could not be found. Please try again later.',
    UserNotCreated: 'The user could not be created. Please try again later.',
    WorkspaceHasActiveBrands:
      'You cannot delete a workspace that has active brands, please delete all brands before deleting the workspace',
    DomainAlreadyConnected:
      'This site already has a connected domain. Please disconnect the current domain before connecting a new one.',
    UserAlreadyExists:
      'A user with that email address already exists in the wprkspace. Please use a different email address.',
    NewsLetterAlreadyExists: 'A newsletter with that name already exists. Please choose a different name.',
    StripeCheckoutError: 'An error occurred while processing the payment. Please try again later.',
    NoSubscriptionsToExport: 'There are no subscriptions to export for this period',
    InvalidSiteId: 'The site id is invalid',
    ExportMultipleBrandError: 'All pages should belong to the same brand',
    ExportNonArticlePageError: 'Only article pages can be exported',
    ExportingData: 'Data will take a few minutes to export. Please check your email for the download link.',
    StripeDonationProductAlreadyExists:
      'This site already has a donation product. Please delete the existing donation product before creating a new one.',
    SubscriptionPlanInUse:
      'This plan is offered for plan switching in the Customer Portal. Remove it from the portal configuration before deleting it.',
  },
  chatbot: {
    defaultGreeting: 'Hello, how can I help you today?',
  },
  seo: {
    sectionTitle: 'The latest {sectionName} News, Stories, and Articles | {siteName}.',
    sectionDescription:
      'The latest {sectionName} News, Stories, and Articles | {siteName}. Stay informed with in-depth coverage of {sectionName} and more. Find compelling stories, insightful analysis, and breaking news in one convenient location.',
    tagTitle: 'Explore {tagName} News, Stories, and Articles | {siteName}.',
    tagDescription:
      'Discover a curated collection of articles related to {tagName} on {siteName}. Stay informed with the latest news, analysis, and insights on [Tag Topic]. Explore diverse perspectives and in-depth coverage of {tagName} in one convenient location.',
    authorTitle: 'Articles, stories, news, by {authorName} | {siteName}.',
    authorDescription:
      "Explore the insightful articles and contributions by {authorName} on {siteName}. Discover compelling stories, expert analysis, and thought-provoking commentary from this accomplished writer. Learn more about {authorName}'s background and expertise in their biography.",
    homepageDescription: 'The latest news, stories, and articles from {siteName}.',
    latestNewsFeedTitle: '{siteName} Latest News',
    feedDescription: 'Content from {domainName}',
    notFoundTitle: 'Page Not Found | {siteName}',
    notFoundDescription: "The page you are looking for doesn't exist.",
    accountTitle: 'My Account | {siteName}',
    accountDescription:
      'Log in to your {siteName} Account · Manage My Account · Manage Your Subscriptions · My Account · Home Delivery & Digital Subscription · My Reading List',
    searchTitle: 'Search | {siteName}',
    searchDescription: "Search page. Use the search box to find what you're looking for.",
    authorFallbackName: 'Author name',
    checkoutTitle: 'Checkout | {siteName}',
    checkoutDescription: 'Subscription checkout process | {siteName}',
    latestArticlesTitle: 'Latest Stories | {siteName}',
    latestArticlesDescription: 'The {siteName} - Latest Breaking News.',
    paymentsTitle: 'Payments | {siteName}',
    paymentsDescription: 'Payments for {siteName}.',
    privacyPolicyTitle: 'Privacy Policy | {siteName}',
    privacyPolicyDescription: 'Privacy Policy | {siteName}.',
    signInTitle: 'Sign-in | {siteName}',
    signInDescription: 'Sign In | {siteName} - Start enjoying your online subscription today.',
    thankYouTitle: 'Sign-in | {siteName}',
    thankYouDescription: 'Thank you for your subscription! ♥ | {siteName}',
    subscribeTitle: 'Subscribe | {siteName}',
    subscribeDescription: 'Subscribe Today | {siteName}.',
    termsTitle: 'Terms of use | {siteName}',
    termsDescription: 'Terms of Service/Use/Sale | {siteName}.',
    contactUsTitle: 'Contact Us | {siteName}',
    contactUsDescription: 'Get in touch. | {siteName}',
    spotlightDefaultName: 'Spotlight',
    spotlightTitle: '{spotlightName} | {siteName}',
    spotlightDescription: 'Featured stories and highlights. | {siteName}',
    eventsTitle: 'Events | {siteName}',
    eventsDescription: 'Upcoming events. | {siteName}',
    allTagsTitle: 'All Tags | {siteName}',
    allTagsDescription: 'Browse all tags. | {siteName}',
    allSectionsTitle: 'All Sections | {siteName}',
    allSectionsDescription: 'Browse all sections. | {siteName}',
    allAuthorsTitle: 'All Authors | {siteName}',
    allAuthorsDescription: 'Meet our writers and contributors. | {siteName}',
    newslettersTitle: 'Newsletters | {siteName}',
    newslettersDescription: 'Subscribe to our newsletters. | {siteName}',
    sitemapTitle: 'Sitemap | {siteName}',
    sitemapDescription: 'Site map and navigation. | {siteName}',
    giftTitle: 'Gift Subscription | {siteName}',
    giftDescription: 'Give a subscription as a gift. | {siteName}',
    giftRedeemTitle: 'Redeem Gift | {siteName}',
    giftRedeemDescription: 'Redeem your gift subscription. | {siteName}',
  },
} as const
